import { GenerationPreset } from "@next-platform/packages-ts-types";
import { Button, ButtonSize, ButtonVariant, Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { generatePresetName, useGenerationPresets } from "@next-platform/client-common-core";
import { Accordion, AccordionItem } from "@nextui-org/react";
import { Key } from "@react-types/shared";
import { useState } from "react";

import Trans from "@/components/Trans/Trans";
import { PresetCard } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager/PresetCard";
import { PresetDeleteDialog } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager/PresetDeleteDialog";
import { PresetEditDialog } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager/PresetEditDialog";
import { PresetSaveDialog } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager/PresetSaveDialog";
import { PresetsManagerProps } from "@/pages/Chat/components/ModalGenerationSettings/PresetsManager/types";

export function PresetsManager({ currentSettings, modelName, onApplyPreset, isDisabled = false }: PresetsManagerProps) {
  const { availablePresets: presets, unavailableModelIds, getModelNameById, onCreatePreset, onUpdatePreset, onDeletePreset } = useGenerationPresets();
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleAccordion = () => {
    setSelectedKeys((prev: Key[]) => (prev.includes("presets") ? [] : ["presets"]));
  };

  const handleSavePreset = () => {
    const suggestedName = generatePresetName(currentSettings, modelName);
    setNewPresetName(suggestedName);
    setShowSaveDialog(true);
  };

  const confirmSavePreset = async () => {
    if (newPresetName.trim() && !isLoading) {
      setIsLoading(true);
      try {
        const result = await onCreatePreset(currentSettings, modelName, newPresetName.trim());
        if (result === null) {
          // Duplicate detected
          setDuplicateError(true);
          setTimeout(() => setDuplicateError(false), 3000); // Clear error after 3 seconds
        } else {
          // Success
          setShowSaveDialog(false);
          setNewPresetName("");
          setDuplicateError(false);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleStartEdit = (preset: GenerationPreset) => {
    setEditingId(preset.id);
    setEditingName(preset.name);
  };

  const handleSaveEdit = async () => {
    if (editingId && editingName.trim() && !isLoading) {
      setIsLoading(true);
      try {
        await onUpdatePreset(editingId, { name: editingName.trim() });
        setEditingId(null);
        setEditingName("");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleDelete = (id: string) => {
    setDeletingId(id);
  };

  const confirmDelete = async () => {
    if (deletingId && !isLoading) {
      setIsLoading(true);
      try {
        await onDeletePreset(deletingId);
        setDeletingId(null);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  return (
    <div className="rounded-lg bg-gray-6">
      <Accordion className="px-0 py-0" selectedKeys={selectedKeys} keepContentMounted>
        <AccordionItem
          key="presets"
          as="div"
          hideIndicator
          classNames={{
            base: "p-0",
            content: "pt-2 pb-0 px-3",
            trigger: "p-0 px-3 py-3",
          }}
          HeadingComponent={() => (
            <div className="flex flex-row justify-between items-center cursor-pointer w-full" onClick={() => !isDisabled && toggleAccordion()}>
              <div className="flex flex-row gap-2 items-center">
                <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="text-gray-12">
                  <Trans i18nKey="chat:modal.generationSettings.presets.title" />
                </Text>
                {presets.length > 0 && <span className="px-2 py-0.5 rounded-full bg-gray-7 text-gray-11 text-xs font-medium">{presets.length}</span>}
              </div>
              <Icon
                icon={selectedKeys.includes("presets") ? IconVariants.CHEVRON_UP : IconVariants.CHEVRON_DOWN}
                size={IconSize.SMALL}
                className="text-gray-11"
              />
            </div>
          )}
        >
          <div className="flex flex-col gap-2">
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
              <Trans i18nKey="chat:modal.generationSettings.presets.description" />
            </Text>

            {presets.length > 0 && (
              <div className="flex flex-col gap-2">
                {presets.map((preset: GenerationPreset) => (
                  <div key={preset.id}>
                    {deletingId === preset.id ? (
                      <PresetDeleteDialog
                        presetName={preset.name}
                        onConfirm={confirmDelete}
                        onCancel={cancelDelete}
                        isDisabled={isDisabled}
                        isDeleting={isLoading}
                      />
                    ) : editingId === preset.id ? (
                      <PresetEditDialog
                        presetName={preset.name}
                        editingName={editingName}
                        onNameChange={setEditingName}
                        onSave={handleSaveEdit}
                        onCancel={handleCancelEdit}
                        isDisabled={isDisabled}
                        isUpdating={isLoading}
                      />
                    ) : (
                      <PresetCard
                        preset={preset}
                        isUnavailable={unavailableModelIds.has(preset.modelId)}
                        getModelName={getModelNameById}
                        onApply={onApplyPreset}
                        onEdit={handleStartEdit}
                        onDelete={handleDelete}
                        isDisabled={isDisabled}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {showSaveDialog ? (
              <PresetSaveDialog
                presetName={newPresetName}
                onNameChange={setNewPresetName}
                onSave={confirmSavePreset}
                onCancel={() => setShowSaveDialog(false)}
                isDisabled={isDisabled}
                isSaving={isLoading}
                duplicateError={duplicateError}
              />
            ) : (
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.OUTLINED}
                iconStart={IconVariants.PLUS}
                onClick={handleSavePreset}
                disabled={isDisabled}
                className="w-full hover:bg-gray-5 dark:hover:bg-gray-6 transition-colors"
              >
                <Trans i18nKey="chat:modal.generationSettings.presets.saveCurrentAsPreset" />
              </Button>
            )}
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
